import { BoundingBox, Vec3, Ray, Mesh as PcMesh } from 'playcanvas';
import { BufferAttribute, BufferGeometry, Matrix4 as ThreeMatrix4, Ray as ThreeRay, Vector3 as ThreeVector3 } from 'three';
import { MeshBVH, SAH } from 'three-mesh-bvh';

import { ElementType } from './element';
import { Mesh } from './mesh';
import { isIOS } from './platform';
import type { Scene } from './scene';

// Simple collider record using world-space AABB
export interface AabbCollider {
    aabb: BoundingBox;
    ref?: any;
}

// Collision Modes
export enum CollisionMode {
    OFF = 'off',
    BOX = 'box', // Camera confined to a box
    SOLID = 'solid', // Cannot pass through objects
    ROADWAY = 'roadway' // Confined to roadway volume
}

// Collect colliders from the scene
export function getSceneColliders(scene: Scene): AabbCollider[] {
    const colliders: AabbCollider[] = [];
    const meshes = scene.getElementsByType(ElementType.model);

    const sources = (scene.events.invoke?.('camera.collisionSources') ?? 'none') as string;
    const includeMeshColliders = sources === 'mesh' || sources === 'all';

    // If the user has disabled mesh collision ("none" or "splats"), do not produce
    // any mesh-based colliders.
    if (!includeMeshColliders) {
        return colliders;
    }

    for (const e of meshes) {
        const m = e as Mesh;
        // Only meshes explicitly marked as blocking participate.
        const collidable = m.collisionMode === 'block';
        if (m.visible && collidable && m.worldBound) {
            colliders.push({ aabb: m.worldBound, ref: m });
        }
    }
    return colliders;
}

// Collect ONLY blocking mesh colliders, independent of any UI collision mode.
// Walk mode uses this so the camera can't pass through walls even if proximity
// guard is set to "none".
export function getBlockingSceneColliders(scene: Scene): AabbCollider[] {
    const colliders: AabbCollider[] = [];
    const meshes = scene.getElementsByType(ElementType.model);
    for (const e of meshes) {
        const m = e as Mesh;
        if (m.visible && m.collisionMode === 'block' && m.worldBound) {
            colliders.push({ aabb: m.worldBound, ref: m });
        }
    }
    return colliders;
}

// Get the specific ground mesh if one is designated
export function getGroundMesh(scene: Scene): Mesh | null {
    const meshes = scene.getElementsByType(ElementType.model);
    for (const e of meshes) {
        const m = e as Mesh;
        if (m.visible && m.collisionMode === 'block') {
            return m;
        }
    }
    return null;
}

// Expand an AABB by scalar r in all directions
function expandedAabbMinMax(aabb: BoundingBox, r: number): { min: Vec3; max: Vec3 } {
    const min = aabb.getMin().clone();
    const max = aabb.getMax().clone();
    min.x -= r; min.y -= r; min.z -= r;
    max.x += r; max.y += r; max.z += r;
    return { min, max };
}

// Slab test for segment vs AABB (returns t)
function segmentAabbIntersectT(p0: Vec3, p1: Vec3, bmin: Vec3, bmax: Vec3): number | null {
    const dir = new Vec3().sub2(p1, p0);
    const invDir = new Vec3(
        dir.x !== 0 ? 1 / dir.x : Number.POSITIVE_INFINITY,
        dir.y !== 0 ? 1 / dir.y : Number.POSITIVE_INFINITY,
        dir.z !== 0 ? 1 / dir.z : Number.POSITIVE_INFINITY
    );

    let tmin = 0;
    let tmax = 1;

    for (let i = 0; i < 3; ++i) {
        const d = (i === 0) ? dir.x : (i === 1) ? dir.y : dir.z;
        const id = (i === 0) ? invDir.x : (i === 1) ? invDir.y : invDir.z;
        const o = (i === 0) ? p0.x : (i === 1) ? p0.y : p0.z;
        const mi = (i === 0) ? bmin.x : (i === 1) ? bmin.y : bmin.z;
        const ma = (i === 0) ? bmax.x : (i === 1) ? bmax.y : bmax.z;

        const t1 = (mi - o) * id;
        const t2 = (ma - o) * id;

        tmin = Math.max(tmin, Math.min(t1, t2));
        tmax = Math.min(tmax, Math.max(t1, t2));
    }

    if (tmax < tmin) return null;
    return tmin;
}

function pointInsideAabb(point: Vec3, bmin: Vec3, bmax: Vec3): boolean {
    return point.x >= bmin.x && point.x <= bmax.x &&
        point.y >= bmin.y && point.y <= bmax.y &&
        point.z >= bmin.z && point.z <= bmax.z;
}

// Resolve movement against solid colliders (sliding/blocking)
export function resolveSolidCollision(scene: Scene, p0: Vec3, p1: Vec3, radius: number, options?: { colliders?: AabbCollider[] }): Vec3 {
    const colliders = options?.colliders ?? getSceneColliders(scene);
    if (colliders.length === 0) return p1;

    const seg = new Vec3().sub2(p1, p0);
    const segLen = seg.length();
    if (segLen < 1e-6) return p1;

    let bestT: number | null = null;
    // Back off from the hit in world units (prevents sticking due to precision).
    const backoff = 1e-3;
    const backoffT = Math.max(0, Math.min(1, backoff / segLen));

    for (const c of colliders) {
        const { min, max } = expandedAabbMinMax(c.aabb, radius);

        // If we start inside this expanded collider, don't treat it as an entry hit.
        // This avoids per-frame rollback to p0 that freezes movement.
        if (pointInsideAabb(p0, min, max)) {
            continue;
        }

        const t = segmentAabbIntersectT(p0, p1, min, max);
        if (t !== null) {
            if (bestT === null || t < bestT) {
                bestT = t;
            }
        }
    }

    if (bestT === null) return p1;

    const tClamped = Math.max(0, Math.min(1, bestT - backoffT));
    const result = new Vec3();
    result.lerp(p0, p1, tClamped);
    return result;
}

// Push camera out of expanded colliders, with a per-frame maximum step.
// This is used for "proximity guard" behavior: if the camera starts (or ends) inside
// a collider's expanded AABB, we push it back opposite the attempted movement.
export function applyProximityPushback(scene: Scene, p0: Vec3, p1: Vec3, clearance: number, maxStep = 2): Vec3 {
    const colliders = getSceneColliders(scene);
    if (colliders.length === 0) return p1;

    const move = new Vec3().sub2(p1, p0);
    const moveLen = move.length();
    if (moveLen < 1e-6) return p1;

    // Back direction (opposite attempted movement)
    move.mulScalar(-1 / moveLen);

    const eps = 1e-3;
    const result = p1.clone();

    // Few iterations to handle overlapping colliders without large teleports.
    for (let iter = 0; iter < 4; iter++) {
        let changed = false;

        for (const c of colliders) {
            const { min, max } = expandedAabbMinMax(c.aabb, clearance);
            if (!pointInsideAabb(result, min, max)) continue;

            // Compute exit distance from inside AABB along the back direction.
            let bestT = Infinity;

            // X axis
            if (move.x !== 0) {
                const tx = (move.x > 0 ? (max.x - result.x) : (min.x - result.x)) / move.x;
                if (tx >= 0 && tx < bestT) bestT = tx;
            }
            // Y axis
            if (move.y !== 0) {
                const ty = (move.y > 0 ? (max.y - result.y) : (min.y - result.y)) / move.y;
                if (ty >= 0 && ty < bestT) bestT = ty;
            }
            // Z axis
            if (move.z !== 0) {
                const tz = (move.z > 0 ? (max.z - result.z) : (min.z - result.z)) / move.z;
                if (tz >= 0 && tz < bestT) bestT = tz;
            }

            if (!Number.isFinite(bestT)) continue;

            const step = Math.min(maxStep, bestT + eps);
            result.x += move.x * step;
            result.y += move.y * step;
            result.z += move.z * step;
            changed = true;
        }

        if (!changed) break;
    }

    return result;
}

export function applyProximityPushbackWithColliders(colliders: AabbCollider[], p0: Vec3, p1: Vec3, clearance: number, maxStep = 2): Vec3 {
    if (colliders.length === 0) return p1;
    // Delegate via the scene-less logic by temporarily wrapping.
    // This mirrors applyProximityPushback's algorithm without coupling it to Scene.
    const move = new Vec3().sub2(p1, p0);
    const moveLen = move.length();
    if (moveLen < 1e-6) return p1;

    move.mulScalar(-1 / moveLen);

    const eps = 1e-3;
    const result = p1.clone();

    for (let iter = 0; iter < 4; iter++) {
        let changed = false;

        for (const c of colliders) {
            const { min, max } = expandedAabbMinMax(c.aabb, clearance);
            if (!pointInsideAabb(result, min, max)) continue;

            let bestT = Infinity;

            if (move.x !== 0) {
                const tx = (move.x > 0 ? (max.x - result.x) : (min.x - result.x)) / move.x;
                if (tx >= 0 && tx < bestT) bestT = tx;
            }
            if (move.y !== 0) {
                const ty = (move.y > 0 ? (max.y - result.y) : (min.y - result.y)) / move.y;
                if (ty >= 0 && ty < bestT) bestT = ty;
            }
            if (move.z !== 0) {
                const tz = (move.z > 0 ? (max.z - result.z) : (min.z - result.z)) / move.z;
                if (tz >= 0 && tz < bestT) bestT = tz;
            }

            if (!Number.isFinite(bestT)) continue;

            const step = Math.min(maxStep, bestT + eps);
            result.x += move.x * step;
            result.y += move.y * step;
            result.z += move.z * step;
            changed = true;
        }

        if (!changed) break;
    }

    return result;
}

// Constrain position to be inside a box
export function constrainToBox(position: Vec3, aabb: BoundingBox, radius: number): Vec3 {
    const min = aabb.getMin();
    const max = aabb.getMax();

    const res = position.clone();

    // Guard against radius exceeding box half-extents (would cause min > max)
    const halfExtents = max.clone().sub(min).mulScalar(0.5);
    const safeRadius = Math.min(radius, Math.min(halfExtents.x, halfExtents.y, halfExtents.z));

    res.x = Math.max(min.x + safeRadius, Math.min(max.x - safeRadius, res.x));
    res.y = Math.max(min.y + safeRadius, Math.min(max.y - safeRadius, res.y));
    res.z = Math.max(min.z + safeRadius, Math.min(max.z - safeRadius, res.z));

    return res;
}

// Intersect ray with a single triangle
// Returns t (distance) or null
const e1 = new Vec3();
const e2 = new Vec3();
const h = new Vec3();
const s = new Vec3();
const q = new Vec3();

function intersectTriangle(origin: Vec3, dir: Vec3, v0: Vec3, v1: Vec3, v2: Vec3): number | null {
    e1.sub2(v1, v0);
    e2.sub2(v2, v0);
    h.cross(dir, e2);
    const a = e1.dot(h);

    if (a > -0.00001 && a < 0.00001) return null;

    const f = 1.0 / a;
    s.sub2(origin, v0);
    const u = f * s.dot(h);

    if (u < 0.0 || u > 1.0) return null;

    q.cross(s, e1);
    const v = f * dir.dot(q);

    if (v < 0.0 || u + v > 1.0) return null;

    const t = f * e2.dot(q);

    if (t > 0.00001) return t;

    return null;
}

// Triangle-accurate raycast against mesh geometry (world space).
// Returns closest hit (smallest positive t), or null if no hit.
//
// NOTE: This intentionally avoids allocations on hot paths and uses a simple
// cached positions/indices array per PlayCanvas Mesh. BVH can be added later.
const _raycastPositionsCache = new WeakMap<any, number[]>();
const _raycastIndicesCache = new WeakMap<any, number[]>();

// BVH cache per PlayCanvas mesh (pcMesh)
const _meshBvhCache = new WeakMap<any, MeshBVH>();
const _meshBvhGeometryCache = new WeakMap<any, BufferGeometry>();
const _triV0 = new Vec3();
const _triV1 = new Vec3();
const _triV2 = new Vec3();

export function raycastSceneMeshes(
    scene: Scene,
    ray: Ray,
    options?: { onlyBlocking?: boolean }
): { mesh: Mesh; position: Vec3; distance: number } | null {
    const onlyBlocking = options?.onlyBlocking ?? false;
    const useBvh = !isIOS();

    const meshes = scene.getElementsByType(ElementType.model).filter((e) => {
        const m = e as Mesh;
        if (!m.visible) return false;
        if (onlyBlocking && m.collisionMode !== 'block') return false;
        return true;
    }) as Mesh[];

    let bestT: number | null = null;
    let bestMesh: Mesh | null = null;

    // Reuse three.js objects to reduce per-ray allocations.
    const worldToLocal = new ThreeMatrix4();
    const localOriginV = new ThreeVector3();
    const localDirV = new ThreeVector3();
    const threeRay = new ThreeRay(localOriginV, localDirV);

    for (const mesh of meshes) {
        if (!mesh.entity.model || !mesh.entity.model.meshInstances) continue;
        for (const meshInstance of mesh.entity.model.meshInstances) {
            const pcMesh = meshInstance.mesh as any as PcMesh;
            if (!pcMesh) continue;

            // Coarse test using the mesh instance world AABB (more reliable than aggregated bounds).
            const instanceAabb = (meshInstance as any).aabb as BoundingBox | undefined;
            if (!instanceAabb || !instanceAabb.intersectsRay(ray)) continue;

            // Use per-instance world transform when available (multi-node GLTF/containers).
            const instanceTransform =
                ((meshInstance as any).node?.getWorldTransform?.() as any) ??
                ((meshInstance as any).node?.worldTransform as any) ??
                mesh.worldTransform;

            // Prepare world->local for this instance (BVH raycast happens in local space).
            // Note: PlayCanvas Mat4 and three.js Matrix4 are column-major and compatible for raw data copy.
            worldToLocal.fromArray(instanceTransform.data).invert();

            if (useBvh) {
                // --- BVH path ---
                // We build BVH lazily per PlayCanvas mesh and reuse it across raycasts.
                let bvh = _meshBvhCache.get(pcMesh);
                if (!bvh) {
                    // Grab (and cache) positions/indices.
                    let positions = _raycastPositionsCache.get(pcMesh);
                    if (!positions) {
                        positions = [];
                        (pcMesh as any).getPositions(positions);
                        _raycastPositionsCache.set(pcMesh, positions);
                    }

                    let indices = _raycastIndicesCache.get(pcMesh);
                    if (!indices) {
                        indices = [];
                        (pcMesh as any).getIndices(indices);
                        _raycastIndicesCache.set(pcMesh, indices);
                    }

                    // Convert to three.js BufferGeometry.
                    const geometry = new BufferGeometry();
                    geometry.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3));
                    geometry.setIndex(indices);
                    _meshBvhGeometryCache.set(pcMesh, geometry);

                    // Build BVH.
                    bvh = new MeshBVH(geometry, { strategy: SAH, maxLeafTris: 10 });
                    _meshBvhCache.set(pcMesh, bvh);
                }

                // Convert PlayCanvas ray to three.js ray in local space.
                const o = ray.origin;
                const d = ray.direction;
                localOriginV.set(o.x, o.y, o.z).applyMatrix4(worldToLocal);

                // For direction: transform as normal (no translation). Use basis from matrix.
                const m = worldToLocal;
                localDirV.set(
                    d.x * m.elements[0] + d.y * m.elements[4] + d.z * m.elements[8],
                    d.x * m.elements[1] + d.y * m.elements[5] + d.z * m.elements[9],
                    d.x * m.elements[2] + d.y * m.elements[6] + d.z * m.elements[10]
                ).normalize();

                const hit = bvh.raycastFirst(threeRay);
                if (hit) {
                    // hit.point is in local coordinates; convert to world.
                    // Use PlayCanvas transform to avoid another three.js matrix.
                    const localP = hit.point;
                    const worldP = new Vec3(localP.x, localP.y, localP.z);
                    instanceTransform.transformPoint(worldP, worldP);

                    const tWorld = worldP.clone().sub(ray.origin).length();
                    if (bestT === null || tWorld < bestT) {
                        bestT = tWorld;
                        bestMesh = mesh;
                    }

                    // Because we only need the closest per mesh, we can continue checking other meshes
                    // but skip brute force triangle loop for this meshInstance.
                    continue;
                }

                // BVH is authoritative: if it misses, there is no triangle hit.
                continue;
            }

            // Cache positions / indices to avoid per-ray allocations.
            let positions = _raycastPositionsCache.get(pcMesh);
            if (!positions) {
                positions = [];
                (pcMesh as any).getPositions(positions);
                _raycastPositionsCache.set(pcMesh, positions);
            }

            let indices = _raycastIndicesCache.get(pcMesh);
            if (!indices) {
                indices = [];
                (pcMesh as any).getIndices(indices);
                _raycastIndicesCache.set(pcMesh, indices);
            }

            for (let i = 0; i < indices.length; i += 3) {
                const i0 = indices[i];
                const i1 = indices[i + 1];
                const i2 = indices[i + 2];

                _triV0.set(positions[i0 * 3], positions[i0 * 3 + 1], positions[i0 * 3 + 2]);
                _triV1.set(positions[i1 * 3], positions[i1 * 3 + 1], positions[i1 * 3 + 2]);
                _triV2.set(positions[i2 * 3], positions[i2 * 3 + 1], positions[i2 * 3 + 2]);

                instanceTransform.transformPoint(_triV0, _triV0);
                instanceTransform.transformPoint(_triV1, _triV1);
                instanceTransform.transformPoint(_triV2, _triV2);

                const t = intersectTriangle(ray.origin, ray.direction, _triV0, _triV1, _triV2);
                if (t !== null && t > 0.00001) {
                    if (bestT === null || t < bestT) {
                        bestT = t;
                        bestMesh = mesh;
                    }
                }
            }
        }
    }

    if (bestT === null || !bestMesh) return null;

    const hitPos = ray.direction.clone().mulScalar(bestT).add(ray.origin);
    return {
        mesh: bestMesh,
        position: hitPos,
        distance: bestT
    };
}

export function getMeshBvhStats(pcMesh: any): { triangles: number; bvhBytes: number; geometryBytes: number } | null {
    const bvh = _meshBvhCache.get(pcMesh);
    const geom = _meshBvhGeometryCache.get(pcMesh);
    if (!bvh || !geom) return null;

    const pos = geom.getAttribute('position');
    const idx = geom.getIndex();

    const triangles = idx ? Math.floor(idx.count / 3) : Math.floor(pos.count / 3);
    const geometryBytes =
        (pos?.array?.byteLength ?? 0) +
        (idx?.array?.byteLength ?? 0);

    // three-mesh-bvh stores ArrayBuffers in SerializedBVH.roots + index.
    const serialized = MeshBVH.serialize(bvh, { cloneBuffers: false });
    const rootsBytes = serialized.roots.reduce((acc, b) => acc + b.byteLength, 0);
    const indexBytes = (serialized.index as any)?.byteLength ?? 0;
    const bvhBytes = rootsBytes + indexBytes;

    return { triangles, bvhBytes, geometryBytes };
}

// Raycast vertically to find ground height at (x, z).
// Returns the Y of the closest blocking mesh hit, or null if none.
export function raycastGroundHeight(scene: Scene, x: number, z: number): number | null {
    const origin = new Vec3(x, 1000, z);
    const dir = new Vec3(0, -1, 0);
    const downRay = new Ray(origin, dir);
    const hit = raycastSceneMeshes(scene, downRay, { onlyBlocking: true });
    return hit ? hit.position.y : null;
}

// Backward-compat helper (kept for any older callers).
export function getGroundHeight(scene: Scene, x: number, yStart: number, z: number): number | null {
    const origin = new Vec3(x, yStart, z);
    const dir = new Vec3(0, -1, 0);
    const downRay = new Ray(origin, dir);
    const hit = raycastSceneMeshes(scene, downRay, { onlyBlocking: true });
    return hit ? hit.position.y : null;
}
